/** Non-instrument `value-mappings` keys whose outputs are fixed by FHIR / US Core. */

import {
  parseHl7LookupKey,
  type InstrumentAnswer,
  type InstrumentGuidance
} from './Hl7Instruments.ts';

export const VALUE_MAPPING_TEMPLATE_CODE_SYSTEMS = {
  /** FHIR R4 `administrative-gender` — Patient.gender's required binding. */
  ADMINISTRATIVE_GENDER: 'http://hl7.org/fhir/administrative-gender',
  /** CDC Race & Ethnicity, the system US Core race/ethnicity codes come from. */
  CDC_RACE_ETHNICITY: 'urn:oid:2.16.840.1.113883.6.238'
} as const;

export type ValueMappingTemplateCodeSystem =
  (typeof VALUE_MAPPING_TEMPLATE_CODE_SYSTEMS)[keyof typeof VALUE_MAPPING_TEMPLATE_CODE_SYSTEMS];

export interface ValueMappingTemplate {
  /** The entry's lookup_key, verbatim. */
  lookupKey: string;
  label: string;
  /** Authority the outputs come from. */
  codeSystem: ValueMappingTemplateCodeSystem;
  /** Short authority name shown beside the label in the editor. */
  systemLabel: string;
  guidance: InstrumentGuidance;
}

export interface ValueMappingTemplatePack {
  /** Pack slug; the prepopulate dialog's source value. */
  name: string;
  label: string;
  description: string;
  templates: readonly ValueMappingTemplate[];
}

// No `defaultInput`: no feed sends these strings, so they are picker options
// for the org's own "don't know" / "prefer not to say" answers, never prefilled.
const ABSENCE_REASON_ANSWERS: readonly InstrumentAnswer[] = [
  { output: 'UNK', label: 'Unknown' },
  { output: 'ASKU', label: 'Asked but unknown' },
  { output: 'asked-declined', label: 'Asked but declined' }
];

/** The `detailed` slice's vocabulary, minus the two OMB categories. */
const DETAILED_ETHNICITY: ReadonlyArray<readonly [string, string]> = [
  ['2137-8', 'Spaniard'],
  ['2138-6', 'Andalusian'],
  ['2139-4', 'Asturian'],
  ['2140-2', 'Castillian'],
  ['2141-0', 'Catalonian'],
  ['2142-8', 'Belearic Islander'],
  ['2143-6', 'Gallego'],
  ['2144-4', 'Valencian'],
  ['2145-1', 'Canarian'],
  ['2146-9', 'Spanish Basque'],
  ['2148-5', 'Mexican'],
  ['2149-3', 'Mexican American'],
  ['2150-1', 'Mexicano'],
  ['2151-9', 'Chicano'],
  ['2152-7', 'La Raza'],
  ['2153-5', 'Mexican American Indian'],
  ['2155-0', 'Central American'],
  ['2156-8', 'Costa Rican'],
  ['2157-6', 'Guatemalan'],
  ['2158-4', 'Honduran'],
  ['2159-2', 'Nicaraguan'],
  ['2160-0', 'Panamanian'],
  ['2161-8', 'Salvadoran'],
  ['2162-6', 'Central American Indian'],
  ['2163-4', 'Canal Zone'],
  ['2165-9', 'South American'],
  ['2166-7', 'Argentinean'],
  ['2167-5', 'Bolivian'],
  ['2168-3', 'Chilean'],
  ['2169-1', 'Colombian'],
  ['2170-9', 'Ecuadorian'],
  ['2171-7', 'Paraguayan'],
  ['2172-5', 'Peruvian'],
  ['2173-3', 'Uruguayan'],
  ['2174-1', 'Venezuelan'],
  ['2175-8', 'South American Indian'],
  ['2176-6', 'Criollo'],
  ['2178-2', 'Latin American'],
  ['2180-8', 'Puerto Rican'],
  ['2182-4', 'Cuban'],
  ['2184-0', 'Dominican']
];

const GENDER_TEMPLATE: ValueMappingTemplate = {
  lookupKey: 'demographics:gender',
  label: 'Administrative Gender',
  codeSystem: VALUE_MAPPING_TEMPLATE_CODE_SYSTEMS.ADMINISTRATIVE_GENDER,
  systemLabel: 'FHIR administrative-gender',
  guidance: {
    resultType: 'string',
    description:
      'Maps the registration answer to a FHIR administrative-gender code. Patient.gender binds ' +
      'to these four codes with no absence reasons, so a feed carrying finer-grained answers ' +
      '(nonbinary, X) either collapses onto `other` or drops — there is no code to invent.',
    inputHint:
      'Registration answer text exactly as the feed sends it (e.g. "Female"). A feed sending ' +
      'HL7 PID-8 codes needs one rule per code (F, M, O, U) instead.',
    answers: [
      { output: 'female', label: 'Female', defaultInput: 'Female' },
      { output: 'male', label: 'Male', defaultInput: 'Male' },
      { output: 'other', label: 'Other', defaultInput: 'Other' },
      { output: 'unknown', label: 'Unknown', defaultInput: 'Unknown' }
    ]
  }
};

// 2131-1 is in the ombCategory value set's expansion even though the IG page's
// prose says otherwise; check the expansion before "correcting" this list.
const RACE_TEMPLATE: ValueMappingTemplate = {
  lookupKey: 'demographics:race',
  label: 'Race',
  codeSystem: VALUE_MAPPING_TEMPLATE_CODE_SYSTEMS.CDC_RACE_ETHNICITY,
  systemLabel: 'US Core race (ombCategory)',
  guidance: {
    resultType: 'string',
    description:
      'Maps the registration answer to a US Core race category. The picker is the whole ' +
      'ombCategory value set: the five OMB categories, Other Race, and the three absence ' +
      'reasons. A detailed race (900+ CDC codes) is not offered — use Custom for one.',
    inputHint:
      'Registration answer text exactly as the feed sends it. A feed sending a detailed race ' +
      '(e.g. "Filipino") needs its own rule rolling it up to the OMB parent (Asian).',
    answers: [
      {
        output: '1002-5',
        label: 'American Indian or Alaska Native',
        defaultInput: 'American Indian or Alaska Native'
      },
      { output: '2028-9', label: 'Asian', defaultInput: 'Asian' },
      {
        output: '2054-5',
        label: 'Black or African American',
        defaultInput: 'Black or African American'
      },
      {
        output: '2076-8',
        label: 'Native Hawaiian or Other Pacific Islander',
        defaultInput: 'Native Hawaiian or Other Pacific Islander'
      },
      { output: '2106-3', label: 'White', defaultInput: 'White' },
      { output: '2131-1', label: 'Other Race', defaultInput: 'Other Race' },
      ...ABSENCE_REASON_ANSWERS
    ]
  }
};

const ETHNICITY_TEMPLATE: ValueMappingTemplate = {
  lookupKey: 'demographics:ethnicity',
  label: 'Ethnicity',
  codeSystem: VALUE_MAPPING_TEMPLATE_CODE_SYSTEMS.CDC_RACE_ETHNICITY,
  systemLabel: 'US Core ethnicity (ombCategory + detailed)',
  guidance: {
    resultType: 'string',
    description:
      'Maps the registration answer to a US Core ethnicity code. Only two codes are OMB ' +
      'categories (Hispanic or Latino, Not Hispanic or Latino) and those are what prefill ' +
      'seeds; the picker also carries the 41 detailed codes and the three absence reasons, so ' +
      'a feed sending "Puerto Rican" or "I prefer not to share" has an option.',
    inputHint:
      'Registration answer text exactly as the feed sends it. Emitting a detailed code commits ' +
      'you to the `detailed` slice — the `ombCategory` slice accepts only the first two.',
    answers: [
      { output: '2135-2', label: 'Hispanic or Latino', defaultInput: 'Hispanic or Latino' },
      { output: '2186-5', label: 'Not Hispanic or Latino', defaultInput: 'Not Hispanic or Latino' },
      ...DETAILED_ETHNICITY.map(([output, label]) => ({ output, label })),
      { output: 'OTH', label: 'Other' },
      ...ABSENCE_REASON_ANSWERS
    ]
  }
};

export const VALUE_MAPPING_TEMPLATE_PACKS: readonly ValueMappingTemplatePack[] = [
  {
    name: 'demographics',
    label: 'Patient Demographics',
    description:
      'Gender, race and ethnicity, output as the codes FHIR and US Core require. Prefill seeds ' +
      'the common categories with the canonical display names as MATCH inputs — replace those ' +
      'with the answer text your feed sends; the OUTPUT side stays a picker of valid codes.',
    templates: [GENDER_TEMPLATE, RACE_TEMPLATE, ETHNICITY_TEMPLATE]
  }
];

/** The template for a lookup key, or null when no pack defines it. */
export function findValueMappingTemplate(lookupKey: string): ValueMappingTemplate | null {
  for (const pack of VALUE_MAPPING_TEMPLATE_PACKS) {
    const template = pack.templates.find(t => t.lookupKey === lookupKey);

    if (template) {
      return template;
    }
  }

  return null;
}

/** Guidance for any value-mapping lookup key: `hl7:*` instrument or template. */
export function guidanceForValueMappingKey(lookupKey: string): InstrumentGuidance | null {
  return (
    parseHl7LookupKey(lookupKey)?.guidance ?? findValueMappingTemplate(lookupKey)?.guidance ?? null
  );
}
